import { useState, useEffect, useRef } from 'react'

interface QueryBoxProps {
  onSubmit: (query: string) => void
  isLoading?: boolean
  autoFocus?: boolean
  disabled?: boolean
}

export default function QueryBox({
  onSubmit,
  isLoading = false,
  autoFocus = true,
  disabled = false
}: QueryBoxProps) {
  const [query, setQuery] = useState('')
  const [showCharWarning, setShowCharWarning] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-focus on load
  useEffect(() => {
    if (autoFocus && textareaRef.current && !disabled) {
      textareaRef.current.focus()
    }
  }, [autoFocus, disabled])

  // Update character warning state
  useEffect(() => {
    setShowCharWarning(query.length >= 500)
  }, [query.length])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim() && !isLoading && !disabled) {
      onSubmit(query.trim())
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSubmit(e as any)
    }
  }

  const handleClear = () => {
    setQuery('')
    if (textareaRef.current) {
      textareaRef.current.focus()
    }
  }

  const canSubmit = query.trim() && !isLoading && !disabled && query.length <= 1000

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a clinical question..."
          disabled={disabled || isLoading}
          className={`w-full h-32 p-4 border rounded-lg resize-none focus:ring-2 focus:border-transparent transition-colors text-gray-900 bg-white placeholder:text-gray-400 ${
            query.length > 1000
              ? 'border-red-300 focus:ring-red-500'
              : showCharWarning
              ? 'border-yellow-300 focus:ring-yellow-500'
              : 'border-gray-300 focus:ring-blue-500'
          } ${disabled || isLoading ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          maxLength={1200} // Allow a bit over 1000 to show the error state
        />

        {/* Clear button - shown when text is present */}
        {query && !isLoading && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            tabIndex={-1}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Bottom bar with character count and submit */}
        <div className="absolute bottom-3 right-3 flex items-center space-x-3">
          {/* Character count */}
          <div className="flex items-center space-x-2">
            <span className={`text-sm font-medium ${
              query.length > 1000
                ? 'text-red-600'
                : showCharWarning
                ? 'text-yellow-600'
                : 'text-gray-400'
            }`}>
              {query.length}/1000
            </span>

            {/* Warning icons */}
            {query.length > 1000 && (
              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {showCharWarning && query.length <= 1000 && (
              <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.866-.833-2.636 0L4.178 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={!canSubmit}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium min-w-20 flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Asking...</span>
              </>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </div>

      {/* Helper text */}
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-gray-500">
          Press Cmd+Enter to submit quickly
        </p>

        {/* Character warnings */}
        {query.length > 1000 && (
          <p className="text-xs text-red-600 font-medium">
            Query too long. Maximum 1000 characters.
          </p>
        )}
        {showCharWarning && query.length <= 1000 && (
          <p className="text-xs text-yellow-600">
            Approaching character limit
          </p>
        )}
      </div>
    </form>
  )
}